/**
 * PURE derivations for the Income/Rent verification modals (design §13/§14) AND
 * the react-pdf verification report — the single shared source for both, so the
 * on-screen modal and the downloaded PDF can't drift. Plain `.ts`, client-safe,
 * no db/server imports: category orders/titles, month bucketing, holder
 * picking, and the design display formats ('$ 6,380' / '$ 2,243.18',
 * 'Feb 26, 2027'). Transaction rows within each section AND the monthly summary
 * cards are ordered newest-first (bank-statement order).
 */
import {
	type VerificationDataItem,
	extractPredictions,
} from '#app/utils/report-data-helpers'
import type { ParsedStepData } from './types'

export type VerificationModalKind = 'income' | 'rent'

export interface VerificationSection {
	status: string | undefined
	items: VerificationDataItem[]
}

/** The modal's slice of `predictions.verificationSummary.<section>`. */
export function parseVerificationSection(
	aiReportData: Record<string, unknown> | null | undefined,
	kind: VerificationModalKind,
): VerificationSection | null {
	const summary = extractPredictions(aiReportData ?? null)?.verificationSummary
	const section =
		kind === 'income' ? summary?.incomeVerification : summary?.rentVerification
	if (!section) return null
	return {
		status: section.status,
		items: Array.isArray(section.data) ? section.data : [],
	}
}

/** Optional demo cap so one report's chart stays 12 months while a longer
 * history (guarantor, through Aug '25) still spans every deposit month. */
export function incomeChartMonthCap(
	aiReportData: Record<string, unknown> | null | undefined,
): number | undefined {
	const section = incomeVerificationExtras(aiReportData)
	const cap = section?.chartMonths
	return typeof cap === 'number' && cap > 0 ? cap : undefined
}

/** Demo pin for the newest chart month. Unset keeps the latest deposit. */
export function incomeChartEndMonth(
	aiReportData: Record<string, unknown> | null | undefined,
): string | undefined {
	const end = incomeVerificationExtras(aiReportData)?.chartEnd
	return typeof end === 'string' && /^\d{4}-\d{2}$/.test(end) ? end : undefined
}

/** Fixed divisor for "Actually In Their Account". Unset averages only the
 * months that have a selected deposit. */
export function incomeAverageMonthCount(
	aiReportData: Record<string, unknown> | null | undefined,
): number | undefined {
	const count = incomeVerificationExtras(aiReportData)?.averageMonths
	return typeof count === 'number' && count > 0 ? count : undefined
}

function incomeVerificationExtras(
	aiReportData: Record<string, unknown> | null | undefined,
): {
	chartMonths?: unknown
	chartEnd?: unknown
	averageMonths?: unknown
} | null {
	const section = extractPredictions(aiReportData ?? null)?.verificationSummary
		?.incomeVerification as
		| {
			chartMonths?: unknown
			chartEnd?: unknown
			averageMonths?: unknown
		}
		| null
		| undefined
	return section ?? null
}

export interface AiDeclaredIncomePeriod {
	netMonthly: number | null
	grossMonthly: number | null
}

export interface AiDeclaredIncome {
	current: AiDeclaredIncomePeriod | null
	previous: AiDeclaredIncomePeriod | null
}

const salvageAmount = (value: unknown): number | null => {
	if (typeof value === 'number') return Number.isFinite(value) ? value : null
	if (typeof value === 'string' && value.trim() !== '') {
		const parsed = Number(value.replace(/[$,\s]/g, ''))
		return Number.isFinite(parsed) ? parsed : null
	}
	return null
}

/**
 * AI-declared income (`verificationSummary.incomeVerification.declaredIncome`)
 * — the AI backend's authoritative net/gross MONTHLY figures per employment
 * period (user 2026-07-19; supersedes the 2026-07-18 annual rendering). The
 * payload's `netMonthly`/`grossMonthly` are preferred; annual-only payloads
 * fall back to `netAnnual/12` / `grossAnnual/12`. Null when the payload
 * predates the field; callers then keep the legacy employment-step derivation
 * (declaredMonthlyIncome / yearlyFromMonthly below). When the block IS
 * present it is authoritative for BOTH tiles — a null period (e.g. no
 * previous employment declared) renders '-' rather than falling back to the
 * step. Never-throw salvage.
 */
export function parseAiDeclaredIncome(
	aiReportData: Record<string, unknown> | null | undefined,
): AiDeclaredIncome | null {
	const section = extractPredictions(aiReportData ?? null)?.verificationSummary
		?.incomeVerification as
		| (Record<string, unknown> & { declaredIncome?: unknown })
		| null
		| undefined
	const declared = section?.declaredIncome
	if (!declared || typeof declared !== 'object' || Array.isArray(declared)) {
		return null
	}
	const period = (raw: unknown): AiDeclaredIncomePeriod | null => {
		if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
		const r = raw as Record<string, unknown>
		const monthly = (direct: unknown, annual: unknown): number | null => {
			const monthlyAmount = salvageAmount(direct)
			if (monthlyAmount != null) return monthlyAmount
			const annualAmount = salvageAmount(annual)
			return annualAmount == null ? null : annualAmount / 12
		}
		const netMonthly = monthly(r.netMonthly, r.netAnnual)
		const grossMonthly = monthly(r.grossMonthly, r.grossAnnual)
		if (netMonthly == null && grossMonthly == null) return null
		return { netMonthly, grossMonthly }
	}
	const d = declared as Record<string, unknown>
	const current = period(d.declaredCurrent)
	const previous = period(d.declaredPrevious)
	if (!current && !previous) return null
	return { current, previous }
}

/** Tile breakdown for an AI-declared period — BOTH figures MONTHLY (user
 * 2026-07-19): Net = after-tax `netMonthly`, Gross = `grossMonthly`. A
 * missing figure/period renders '-'. Shared by the income modal and the
 * verification PDF so the two tiles can't drift. */
export function aiDeclaredBreakdown(
	period: AiDeclaredIncomePeriod | null,
): Array<{ amount: string; caption: string }> {
	return [
		{ amount: formatWholeAmount(period?.netMonthly ?? null), caption: 'Net' },
		{
			amount: formatWholeAmount(period?.grossMonthly ?? null),
			caption: 'Gross',
		},
	]
}

/** Category render / stack order — fixed regardless of filter click order.
 * `side_gig` is first-class when the AI emits it (chip/section hidden otherwise). */
export const INCOME_CATEGORY_ORDER = [
	'verified_income',
	'govt_benefits',
	'potential_income',
	'side_gig',
] as const

export type IncomeCategoryKey = (typeof INCOME_CATEGORY_ORDER)[number]

/** Salaried (not self-employed) modal open-state default filters. */
const SALARIED_INCOME_DEFAULTS: readonly IncomeCategoryKey[] = [
	'verified_income',
	'govt_benefits',
]

/** Self-employed modal open-state default filters. */
const SELF_EMPLOYED_INCOME_DEFAULTS: readonly IncomeCategoryKey[] = [
	'verified_income',
	'govt_benefits',
	'potential_income',
]

/**
 * True when employment type is Self-Employed (form value or legacy slug).
 * Drives income modal open-state category defaults.
 */
export function isSelfEmployedEmploymentType(
	employmentType: string | null | undefined,
): boolean {
	if (!employmentType) return false
	const normalized = employmentType.trim().toLowerCase().replace(/_/g, '-')
	return normalized === 'self-employed'
}

const RENT_CATEGORY_ORDER = [
	'rent_verified',
	'confirmed_rent',
	'mortgage',
	'rent_timing_fallback',
	'potential_rent',
] as const

export interface VerificationTableGroup {
	title: string
	/** Design superscript footnote marker ('1'|'2'|'3'); '' when none. */
	sup: string
	/** Stable category key when the group maps 1:1 to a known category. */
	category?: string
	items: VerificationDataItem[]
}

/** Short chip / legend labels (Figma filter bar). */
export const INCOME_FILTER_LABELS: Record<IncomeCategoryKey, string> = {
	verified_income: 'Verified Income',
	govt_benefits: 'Government Benefits',
	potential_income: 'Potentially Related To Income',
	side_gig: 'Side Gig',
}

/** Stacked-chart / filter-dot colors aligned to the Figma redesign. */
export const INCOME_CATEGORY_COLORS: Record<IncomeCategoryKey, string> = {
	verified_income: '#16A34A',
	govt_benefits: '#9976B8',
	potential_income: '#BE7B97',
	side_gig: '#A5BB83',
}

/** Month-list category pills — pastel fill + colored text (not solid). */
export const INCOME_CATEGORY_PILL_STYLES: Record<
	IncomeCategoryKey,
	{ bg: string; text: string }
> = {
	verified_income: {
		bg: '#16A34A1A',
		text: INCOME_CATEGORY_COLORS.verified_income,
	},
	govt_benefits: {
		bg: '#9976B81A',
		text: INCOME_CATEGORY_COLORS.govt_benefits,
	},
	potential_income: {
		bg: '#BE7B971A',
		text: INCOME_CATEGORY_COLORS.potential_income,
	},
	side_gig: { bg: '#A5BB831A', text: INCOME_CATEGORY_COLORS.side_gig },
}

const INCOME_TITLES: Record<string, { title: string; sup: string }> = {
	verified_income: { title: 'Verified Income Pattern', sup: '3' },
	govt_benefits: { title: 'Government Benefits', sup: '' },
	potential_income: {
		title: 'Deposits Potentially Related to Income',
		sup: '2',
	},
	side_gig: { title: 'Side Gig', sup: '' },
}

const INCOME_CATEGORY_SET = new Set<string>(INCOME_CATEGORY_ORDER)

/** Known income category keys present in `items`, in display order. */
export function presentIncomeCategories(
	items: VerificationDataItem[],
): IncomeCategoryKey[] {
	const seen = new Set<string>()
	for (const item of items) {
		if (item.category && INCOME_CATEGORY_SET.has(item.category)) {
			seen.add(item.category)
		}
	}
	return INCOME_CATEGORY_ORDER.filter((key) => seen.has(key))
}

/**
 * Modal open-state income filter selection (user can change chips afterward;
 * modal PDF download passes the live selection via `?categories=`).
 *
 * - Salaried: Verified + Government Benefits when any of those are present;
 *   otherwise every present category except `potential_income`.
 * - Self-employed: Verified + Government + Potential when any of those are
 *   present; otherwise every present category.
 */
export function defaultIncomeCategorySelection(
	present: readonly IncomeCategoryKey[],
	options?: { selfEmployed?: boolean },
): IncomeCategoryKey[] {
	if (present.length === 0) return []
	const preferred = options?.selfEmployed
		? SELF_EMPLOYED_INCOME_DEFAULTS
		: SALARIED_INCOME_DEFAULTS
	const selected = preferred.filter((key) => present.includes(key))
	if (selected.length > 0) return selected
	if (options?.selfEmployed) return [...present]
	return present.filter((key) => key !== 'potential_income')
}

/** Selected income keys in fixed stack/legend order (ignores click order). */
export function orderedIncomeCategories(
	selected: readonly string[],
): IncomeCategoryKey[] {
	const wanted = new Set(selected)
	return INCOME_CATEGORY_ORDER.filter((key) => wanted.has(key))
}

/**
 * Filter income rows to the selected categories.
 * - `null` / `undefined` → all known categories present in items (ZIP /
 *   unfiltered download).
 * - `[]` → no rows (modal deselected all).
 * - non-empty → intersection with known keys; unknown keys ignored.
 */
export function filterIncomeItems(
	items: VerificationDataItem[],
	categories: readonly string[] | null | undefined,
): VerificationDataItem[] {
	if (categories == null) {
		const allowed = new Set<string>(presentIncomeCategories(items))
		if (allowed.size === 0) return []
		return items.filter(
			(item) => item.category != null && allowed.has(item.category),
		)
	}
	if (categories.length === 0) return []
	const allowed = new Set(
		categories.filter((key) => INCOME_CATEGORY_SET.has(key)),
	)
	if (allowed.size === 0) return []
	return items.filter(
		(item) => item.category != null && allowed.has(item.category),
	)
}

/**
 * Parse `?categories=verified_income,govt_benefits` — drops unknown keys,
 * dedupes, preserves display order. Null when the param is absent/blank
 * (caller treats as "all").
 */
export function parseIncomeCategoryFilterParam(
	raw: string | null | undefined,
): IncomeCategoryKey[] | null {
	if (raw == null || raw.trim() === '') return null
	const requested = new Set(
		raw
			.split(',')
			.map((part) => part.trim())
			.filter(Boolean),
	)
	const ordered = INCOME_CATEGORY_ORDER.filter((key) => requested.has(key))
	return ordered.length > 0 ? ordered : null
}

/** Human labels for the active filter set (PDF "Showing: …" line). */
export function incomeCategoryFilterLabels(
	categories: readonly string[] | null | undefined,
	items: VerificationDataItem[],
): string[] {
	const keys =
		categories == null
			? presentIncomeCategories(items)
			: INCOME_CATEGORY_ORDER.filter((key) => categories.includes(key))
	return keys.map((key) => INCOME_FILTER_LABELS[key])
}

const RENT_TITLES: Record<string, { title: string; sup: string }> = {
	rent_verified: {
		title: 'Payments Strongly Consistent with Declared Rent',
		sup: '1',
	},
	confirmed_rent: {
		title: 'Payments Strongly Consistent with Declared Rent',
		sup: '1',
	},
	mortgage: {
		title: 'Payments Strongly Consistent with Declared Rent',
		sup: '1',
	},
	rent_timing_fallback: {
		title: 'Possible Rent-Related Transactions',
		sup: '2',
	},
	potential_rent: { title: 'Possible Rent-Related Transactions', sup: '2' },
}

/** UI filter keys — map to pipeline categories (Figma rent redesign). */
export const RENT_FILTER_ORDER = ['strong_match', 'possible_match'] as const

export type RentFilterKey = (typeof RENT_FILTER_ORDER)[number]

export const RENT_FILTER_LABELS: Record<RentFilterKey, string> = {
	strong_match: 'Strong Match',
	possible_match: 'Include Possible Matches',
}

export const RENT_FILTER_COLORS: Record<RentFilterKey, string> = {
	strong_match: '#16A34A',
	possible_match: '#BE7B97',
}

const RENT_STRONG_CATEGORIES = new Set([
	'rent_verified',
	'confirmed_rent',
	'mortgage',
])
const RENT_POSSIBLE_CATEGORIES = new Set([
	'rent_timing_fallback',
	'potential_rent',
])

export type RentMonthStatus = 'strong' | 'possible' | 'none' | 'empty'

export interface RentMonthCell {
	key: string
	shortLabel: string
	label: string
	amount: number | null
	status: RentMonthStatus
	items?: VerificationDataItem[]
}

export const RENT_STATUS_STYLES: Record<
	RentMonthStatus,
	{ bg: string; text: string; dot: string }
> = {
	// Opaque pastels — Figma rent redesign (alpha fills wash out on white).
	strong: { bg: '#F0FDF4', text: '#16A34A', dot: '#16A34A' },
	possible: { bg: '#F6ABCB1A', text: '#BE7B97', dot: '#BE7B97' },
	// Gap inside declared rental history — red "No Payment Found".
	none: { bg: '#FDF4F5', text: '#DC2626', dot: '#DC2626' },
	// Padded month outside the rental-history span — gray with "-".
	empty: { bg: '#F0F0EE', text: '#747474', dot: '#C9C9C7' },
}

export const RENT_MATCH_PILL_STYLES: Record<
	'strong' | 'possible',
	{ bg: string; text: string; label: string }
> = {
	strong: { bg: '#F0FDF4', text: '#16A34A', label: 'Strong Match' },
	possible: {
		bg: '#F6ABCB1A',
		text: '#BE7B97',
		label: 'Possible Match',
	},
}

export function rentMatchKind(
	category: string | null | undefined,
): 'strong' | 'possible' | null {
	if (!category) return null
	if (RENT_STRONG_CATEGORIES.has(category)) return 'strong'
	if (RENT_POSSIBLE_CATEGORIES.has(category)) return 'possible'
	return null
}

/** Known rent filter keys present in `items`, in display order. */
export function presentRentFilters(
	items: VerificationDataItem[],
): RentFilterKey[] {
	const seen = new Set<RentFilterKey>()
	for (const item of items) {
		const kind = rentMatchKind(item.category)
		if (kind === 'strong') seen.add('strong_match')
		if (kind === 'possible') seen.add('possible_match')
	}
	return RENT_FILTER_ORDER.filter((key) => seen.has(key))
}

/**
 * Modal open-state rent filter selection.
 * Prefer Strong Match only when present; otherwise all present filters.
 */
export function defaultRentFilterSelection(
	present: readonly RentFilterKey[],
): RentFilterKey[] {
	if (present.length === 0) return []
	if (present.includes('strong_match')) return ['strong_match']
	return [...present]
}

/** Selected rent keys in fixed chip order (ignores click order). */
export function orderedRentFilters(
	selected: readonly string[],
): RentFilterKey[] {
	const wanted = new Set(selected)
	return RENT_FILTER_ORDER.filter((key) => wanted.has(key))
}

/**
 * Filter rent rows to the selected UI filters.
 * - `null` / `undefined` → all known filters present in items.
 * - `[]` → no rows.
 */
export function filterRentItems(
	items: VerificationDataItem[],
	filters: readonly string[] | null | undefined,
): VerificationDataItem[] {
	const allowedCategories = (keys: Set<RentFilterKey>): Set<string> => {
		const cats = new Set<string>()
		if (keys.has('strong_match')) {
			for (const c of RENT_STRONG_CATEGORIES) cats.add(c)
		}
		if (keys.has('possible_match')) {
			for (const c of RENT_POSSIBLE_CATEGORIES) cats.add(c)
		}
		return cats
	}
	if (filters == null) {
		const allowed = allowedCategories(new Set(presentRentFilters(items)))
		if (allowed.size === 0) return []
		return items.filter(
			(item) => item.category != null && allowed.has(item.category),
		)
	}
	if (filters.length === 0) return []
	const keys = new Set(
		filters.filter((key): key is RentFilterKey =>
			RENT_FILTER_ORDER.includes(key as RentFilterKey),
		),
	)
	if (keys.size === 0) return []
	const allowed = allowedCategories(keys)
	return items.filter(
		(item) => item.category != null && allowed.has(item.category),
	)
}

/** Parse `?filters=strong_match,possible_match` for rent PDF downloads. */
export function parseRentFilterParam(
	raw: string | null | undefined,
): RentFilterKey[] | null {
	if (raw == null || raw.trim() === '') return null
	const requested = new Set(
		raw
			.split(',')
			.map((part) => part.trim())
			.filter(Boolean),
	)
	const ordered = RENT_FILTER_ORDER.filter((key) => requested.has(key))
	return ordered.length > 0 ? ordered : null
}

export function rentFilterLabels(
	filters: readonly string[] | null | undefined,
	items: VerificationDataItem[],
): string[] {
	const keys =
		filters == null
			? presentRentFilters(items)
			: RENT_FILTER_ORDER.filter((key) => filters.includes(key))
	return keys.map((key) => RENT_FILTER_LABELS[key])
}

function rentMonthStatusForItems(
	monthItems: VerificationDataItem[],
): Exclude<RentMonthStatus, 'empty'> {
	let hasPossible = false
	for (const item of monthItems) {
		const kind = rentMatchKind(item.category)
		if (kind === 'strong') return 'strong'
		if (kind === 'possible') hasPossible = true
	}
	return hasPossible ? 'possible' : 'none'
}

function rentMonthBounds(items: VerificationDataItem[]): {
	earliest: Date
	latest: Date
} | null {
	let earliestMs = Number.POSITIVE_INFINITY
	let latestMs = Number.NEGATIVE_INFINITY
	for (const item of items) {
		const d = new Date(item.date)
		if (Number.isNaN(d.getTime())) continue
		const start = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)
		if (start < earliestMs) earliestMs = start
		if (start > latestMs) latestMs = start
	}
	if (!Number.isFinite(earliestMs) || !Number.isFinite(latestMs)) return null
	return { earliest: new Date(earliestMs), latest: new Date(latestMs) }
}

function parseYearMonth(value: string | undefined): Date | null {
	if (!value || !/^\d{4}-\d{2}$/.test(value)) return null
	const [year, month] = value.split('-').map(Number)
	if (!year || !month || month < 1 || month > 12) return null
	return new Date(Date.UTC(year, month - 1, 1))
}

/** Demo rent grids pin the newest tile (bad report starts at Aug '26). */
export function rentGridEndMonth(
	aiReportData: Record<string, unknown> | null | undefined,
): string | undefined {
	const section = extractPredictions(aiReportData ?? null)?.verificationSummary
		?.rentVerification as { rentGridEnd?: unknown } | null | undefined
	const end = section?.rentGridEnd
	return typeof end === 'string' && /^\d{4}-\d{2}$/.test(end) ? end : undefined
}

/** Always 12 month tiles (newest→oldest), ending at the latest txn month
 * (or the current month when empty).
 * - Inside rental-history span: match → green/pink + actual amount paid;
 *   missing payment → red + declared rent.
 * - Outside that span (padding to fill 12): gray with "-".
 * `endMonth` (YYYY-MM) is a per-report window so the bad sample can start at
 * Aug '26 and show "-" for months with no payment, without changing other
 * reports. */
export function buildRentMonthlyGrid(
	items: VerificationDataItem[],
	declaredRent: number | null,
	options?: { endMonth?: string },
): RentMonthCell[] {
	const bounds = rentMonthBounds(items)
	const pinned = parseYearMonth(options?.endMonth)
	const latest =
		pinned ??
		bounds?.latest ??
		new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1))
	const earliestMs = bounds?.earliest.getTime() ?? null
	const latestMs = bounds?.latest.getTime() ?? null

	const byMonth = new Map<string, VerificationDataItem[]>()
	for (const item of items) {
		const d = new Date(item.date)
		if (Number.isNaN(d.getTime())) continue
		const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
		const arr = byMonth.get(key) ?? []
		arr.push(item)
		byMonth.set(key, arr)
	}

	const cells: RentMonthCell[] = []
	for (let offset = 0; offset <= 11; offset++) {
		const date = new Date(
			Date.UTC(latest.getUTCFullYear(), latest.getUTCMonth() - offset, 1),
		)
		const monthMs = date.getTime()
		const key = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
		const monthShort = date.toLocaleDateString('en-US', {
			month: 'short',
			timeZone: 'UTC',
		})
		const yearShort = String(date.getUTCFullYear()).slice(-2)
		const label = date.toLocaleDateString('en-US', {
			month: 'long',
			year: 'numeric',
			timeZone: 'UTC',
		})

		const inHistory =
			earliestMs != null &&
			latestMs != null &&
			monthMs >= earliestMs &&
			monthMs <= latestMs

		const monthItems = byMonth.get(key) ?? []
		const dashMissing = pinned != null && monthItems.length === 0
		if (!inHistory || dashMissing) {
			cells.push({
				key,
				shortLabel: `${monthShort} '${yearShort}`,
				label,
				amount: null,
				// Pinned windows keep no-payment months visible as a dash
				// (template "No Payment Found"), not hidden padding.
				status: dashMissing ? 'none' : 'empty',
				items: monthItems,
			})
			continue
		}
		const status = rentMonthStatusForItems(monthItems)
		const paidTotal = monthItems.reduce(
			(acc, row) => acc + (row.amount || 0),
			0,
		)
		cells.push({
			key,
			shortLabel: `${monthShort} '${yearShort}`,
			label,
			// Match months: actual AI rent paid. Red (none): declared rent.
			amount:
				status === 'none' ? declaredRent : paidTotal > 0 ? paidTotal : null,
			status,
			items: monthItems,
		})
	}
	return cells
}

/** Out-of-history padding tiles — hidden in UI/PDF; grid builder still emits them. */
export function visibleRentMonthCells(
	months: RentMonthCell[],
): RentMonthCell[] {
	return months.filter((cell) => cell.status !== 'empty')
}

/** '7 Aug, 2025' — rent table date format (Figma redesign). */
export function formatRentTxnDate(value: string | null | undefined): string {
	if (!value) return '-'
	const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
		? new Date(`${value}T12:00:00Z`)
		: new Date(value)
	if (Number.isNaN(d.getTime())) return value
	const day = d.toLocaleDateString('en-GB', {
		day: 'numeric',
		timeZone: 'America/Toronto',
	})
	const month = d.toLocaleDateString('en-GB', {
		month: 'short',
		timeZone: 'America/Toronto',
	})
	const year = d.toLocaleDateString('en-GB', {
		year: 'numeric',
		timeZone: 'America/Toronto',
	})
	return `${day} ${month}, ${year}`
}

function fallbackTitle(category: string): string {
	return category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Sort key for a transaction's date — date-only strings pin to noon UTC
 * (formatTxnDate parity). Unparseable dates sort to -Infinity so they trail
 * the dated rows under the newest-first ordering. */
function txnSortValue(value: string | null | undefined): number {
	if (!value) return Number.NEGATIVE_INFINITY
	const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
		? new Date(`${value}T12:00:00Z`)
		: new Date(value)
	const t = d.getTime()
	return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t
}

/**
 * Category sections in PDF order, merged by design TITLE (the rent design
 * shows two sections while the pipeline emits up to four categories).
 * Unknown categories trail in first-seen order with title-cased names.
 */
export function groupVerificationItems(
	items: VerificationDataItem[],
	kind: VerificationModalKind,
): VerificationTableGroup[] {
	const order: readonly string[] =
		kind === 'income' ? INCOME_CATEGORY_ORDER : RENT_CATEGORY_ORDER
	const titles = kind === 'income' ? INCOME_TITLES : RENT_TITLES
	const byCategory = new Map<string, VerificationDataItem[]>()
	for (const item of items) {
		const category = item.category || 'other'
		const arr = byCategory.get(category) ?? []
		arr.push(item)
		byCategory.set(category, arr)
	}
	const groups: VerificationTableGroup[] = []
	const append = (category: string, list: VerificationDataItem[]) => {
		const meta = titles[category] ?? {
			title: fallbackTitle(category),
			sup: '',
		}
		const existing = groups.find((g) => g.title === meta.title)
		if (existing) {
			existing.items.push(...list)
		} else {
			groups.push({
				...meta,
				category: titles[category] ? category : undefined,
				items: [...list],
			})
		}
	}
	for (const category of order) {
		const list = byCategory.get(category)
		if (list?.length) {
			append(category, list)
			byCategory.delete(category)
		}
	}
	for (const [category, list] of byCategory) {
		if (list.length) append(category, list)
	}
	// Rows read like a bank statement — newest transaction first within each
	// section (the pipeline's source order is otherwise undefined).
	for (const group of groups) {
		group.items.sort((a, b) => txnSortValue(b.date) - txnSortValue(a.date))
	}
	return groups
}

export interface MonthBucket {
	label: string // 'January 2026'
	total: number
}

/** Year-month buckets, DESCENDING (newest month first) — matches the
 * bank-statement order of the transaction tables. (Deviation from the design
 * mock's January→June ascending layout, by request 2026-06-24.) */
export function bucketByMonth(items: VerificationDataItem[]): MonthBucket[] {
	const buckets = new Map<string, { date: Date; total: number }>()
	for (const item of items) {
		const d = new Date(item.date)
		if (Number.isNaN(d.getTime())) continue
		const key = `${d.getUTCFullYear()}-${d.getUTCMonth()}`
		const existing = buckets.get(key)
		if (existing) {
			existing.total += item.amount || 0
		} else {
			buckets.set(key, {
				date: new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)),
				total: item.amount || 0,
			})
		}
	}
	return [...buckets.values()]
		.sort((a, b) => b.date.getTime() - a.date.getTime())
		.map(({ date, total }) => ({
			label: date.toLocaleDateString('en-CA', {
				month: 'long',
				year: 'numeric',
				timeZone: 'UTC',
			}),
			total,
		}))
}

/** 'Feb 26, 2027' (design table format). Date-only strings pin to noon UTC
 * so Toronto rendering can't shift the day (PDF formatIsoDate parity). */
export function formatTxnDate(value: string | null | undefined): string {
	if (!value) return '-'
	const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
		? new Date(`${value}T12:00:00Z`)
		: new Date(value)
	if (Number.isNaN(d.getTime())) return value
	return d.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		timeZone: 'America/Toronto',
	})
}

/** '$ 6,380' — whole dollars with the design's spaced dollar sign. Used for
 * declared figures and monthly roll-ups (typically round). */
export function formatWholeAmount(value: number | null | undefined): string {
	if (value == null || !Number.isFinite(value)) return '-'
	return `$ ${Math.round(value).toLocaleString('en-CA', {
		maximumFractionDigits: 0,
	})}`
}

/** '$ 2,243' — spaced dollar sign, whole dollars (no cents), for individual
 * transaction amounts. */
export function formatTxnAmount(value: number | null | undefined): string {
	if (value == null || !Number.isFinite(value)) return '-'
	return `$ ${value.toLocaleString('en-CA', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	})}`
}

const toTitleCase = (name: string): string => {
	const trimmed = name.trim()
	if (!trimmed) return ''
	if (trimmed !== trimmed.toUpperCase()) return trimmed
	return trimmed.toLowerCase().replace(/\b\p{L}/gu, (c) => c.toUpperCase())
}

/** Most-common holder across the transactions — the bank data is the source
 * of truth for the account name (PDF pickHolder parity). */
export function pickAccountHolder(items: VerificationDataItem[]): string {
	const counts = new Map<string, number>()
	for (const item of items) {
		const name = item.holder?.trim()
		if (!name) continue
		counts.set(name, (counts.get(name) ?? 0) + 1)
	}
	let best = ''
	let bestCount = 0
	for (const [name, count] of counts) {
		if (count > bestCount) {
			best = name
			bestCount = count
		}
	}
	return toTitleCase(best)
}

const toNumber = (value: string | undefined): number | null => {
	if (!value) return null
	const numeric = Number(value.replace(/[$,\s]/g, ''))
	return Number.isFinite(numeric) && numeric > 0 ? numeric : null
}

/** Yearly salary stated on the application (current job + additional jobs). */
export function declaredAnnualIncome(
	employment: ParsedStepData['employment'],
): number | null {
	if (!employment) return null
	let total = toNumber(employment.annualIncome) ?? 0
	for (const extra of employment.additionalEmployments ?? []) {
		total += toNumber(extra.annualIncome) ?? 0
	}
	return total > 0 ? total : null
}

/** 'Declared Current' = (current annualIncome + additional employments) / 12;
 * null when nothing was declared. This is the 'Monthly' figure; the 'Gross'
 * (yearly) figure is this × 12. */
export function declaredMonthlyIncome(
	employment: ParsedStepData['employment'],
): number | null {
	if (!employment) return null
	let total = toNumber(employment.annualIncome) ?? 0
	for (const extra of employment.additionalEmployments ?? []) {
		total += toNumber(extra.annualIncome) ?? 0
	}
	return total > 0 ? total / 12 : null
}

/** 'Declared Previous' = previous annual income / 12; null when no previous
 * income was declared. The 'Monthly' figure; the 'Gross' (yearly) figure is
 * this × 12. */
export function declaredPreviousMonthlyIncome(
	employment: ParsedStepData['employment'],
): number | null {
	if (!employment) return null
	const annual = toNumber(employment.previousAnnualIncome)
	return annual != null ? annual / 12 : null
}

/** Yearly (Gross) figure for the income tiles: simply the monthly (Net) × 12
 * (user 2026-06-24). null passes through. */
export function yearlyFromMonthly(monthly: number | null): number | null {
	return monthly != null ? monthly * 12 : null
}

/** Numeric declared rent from the rental step (current or previous). */
export function declaredRentAmount(
	rental: ParsedStepData['rental'],
	period: 'current' | 'previous' = 'current',
): number | null {
	return toNumber(
		period === 'previous' ? rental?.previousRent : rental?.currentRent,
	)
}

/** '$ 1,800/month' from the rental step's declared rent — the current rent by
 * default, or the previous tenancy's rent for the 'Previous' tile (§14). Null
 * when that period's rent wasn't declared (tile renders '-'). */
export function declaredRentLabel(
	rental: ParsedStepData['rental'],
	period: 'current' | 'previous' = 'current',
): string | null {
	const rent = declaredRentAmount(rental, period)
	return rent != null ? `${formatWholeAmount(rent)}/month` : null
}

/** Average monthly deposit total. By default this is the selected total
 * divided by months that have a selected deposit. `monthCount` keeps the
 * connection's history length (the pre-screen sample divides by 13 even
 * when Side Gig adds a partial month). Null when there are no dated rows
 * and no fixed count. */
export function averageMonthlyDeposits(
	items: VerificationDataItem[],
	options?: { monthCount?: number },
): number | null {
	const buckets = bucketByMonth(items)
	const monthCount = options?.monthCount
	if (monthCount != null && monthCount > 0) {
		if (buckets.length === 0) return 0
		const sum = buckets.reduce((acc, b) => acc + b.total, 0)
		return sum / monthCount
	}
	if (buckets.length === 0) return null
	const sum = buckets.reduce((acc, b) => acc + b.total, 0)
	return sum / buckets.length
}

export interface StackedMonthSeriesRow {
	/** Chart axis: "Sep '25" */
	shortLabel: string
	/** Tooltip / table: 'October 2025' */
	label: string
	/** Sort key YYYY-MM */
	key: string
	total: number
	/** Per-category amounts (0 when absent that month). */
	byCategory: Record<IncomeCategoryKey, number>
}

/** Per-month stacked series for the Recharts chart and the PDF deposit chart.
 * At least 12 months ending at the latest dated deposit in `spanItems`
 * (defaults to `items`), newest month first. A longer history (the guarantor
 * sample spans 13 months, through Aug '25) keeps every month that has a
 * deposit, unless `maxMonths` caps the axis (the bad report stops at Sep '25).
 * `endMonth` pins the newest bar (pre-screen ends at Aug '26, so Sep '26
 * and Aug '25 stay off that chart).
 * Months with no matching deposits render as zero-height bars so filter
 * toggles keep a stable axis. */
export function buildStackedMonthlySeries(
	items: VerificationDataItem[],
	options?: {
		spanItems?: VerificationDataItem[]
		maxMonths?: number
		/** YYYY-MM. Unset ends the axis on the latest deposit month. */
		endMonth?: string
	},
): StackedMonthSeriesRow[] {
	const emptyCats = (): Record<IncomeCategoryKey, number> => ({
		verified_income: 0,
		govt_benefits: 0,
		potential_income: 0,
		side_gig: 0,
	})

	const monthStartUtc = (d: Date) =>
		Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)

	const spanSource = options?.spanItems ?? items
	let latestMs = Number.NEGATIVE_INFINITY
	let earliestMs = Number.POSITIVE_INFINITY
	for (const item of spanSource) {
		const d = new Date(item.date)
		if (Number.isNaN(d.getTime())) continue
		const start = monthStartUtc(d)
		if (start > latestMs) latestMs = start
		if (start < earliestMs) earliestMs = start
	}
	const pinnedEnd = parseYearMonth(options?.endMonth)
	if (pinnedEnd) latestMs = pinnedEnd.getTime()
	if (!Number.isFinite(latestMs)) return []

	const latest = new Date(latestMs)
	const earliest = new Date(earliestMs)
	const spanMonths =
		(latest.getUTCFullYear() - earliest.getUTCFullYear()) * 12 +
		(latest.getUTCMonth() - earliest.getUTCMonth()) +
		1
	const monthCount = options?.maxMonths ?? Math.max(12, spanMonths)
	const buckets = new Map<
		string,
		{ date: Date; byCategory: Record<IncomeCategoryKey, number>; total: number }
	>()
	for (let offset = 0; offset < monthCount; offset++) {
		const date = new Date(
			Date.UTC(latest.getUTCFullYear(), latest.getUTCMonth() - offset, 1),
		)
		const key = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
		buckets.set(key, { date, byCategory: emptyCats(), total: 0 })
	}

	for (const item of items) {
		const d = new Date(item.date)
		if (Number.isNaN(d.getTime())) continue
		const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
		const existing = buckets.get(key)
		if (!existing) continue
		const amount = item.amount || 0
		existing.total += amount
		const cat = item.category
		if (cat && INCOME_CATEGORY_SET.has(cat)) {
			existing.byCategory[cat as IncomeCategoryKey] += amount
		}
	}

	return [...buckets.entries()].map(([key, { date, byCategory, total }]) => {
		const monthShort = date.toLocaleDateString('en-US', {
			month: 'short',
			timeZone: 'UTC',
		})
		const yearShort = String(date.getUTCFullYear()).slice(-2)
		return {
			key,
			shortLabel: `${monthShort} '${yearShort}`,
			label: date.toLocaleDateString('en-US', {
				month: 'long',
				year: 'numeric',
				timeZone: 'UTC',
			}),
			total,
			byCategory,
		}
	})
}

export interface MonthTransactionGroup {
	/** 'Aug 2025' */
	label: string
	key: string
	depositCount: number
	total: number
	items: VerificationDataItem[]
}

/** Month-by-month transaction list (newest month first). Rows within each
 * month are newest-first. */
export function groupTransactionsByMonth(
	items: VerificationDataItem[],
): MonthTransactionGroup[] {
	const buckets = new Map<
		string,
		{ date: Date; items: VerificationDataItem[] }
	>()
	for (const item of items) {
		const d = new Date(item.date)
		if (Number.isNaN(d.getTime())) continue
		const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
		const existing = buckets.get(key)
		if (existing) {
			existing.items.push(item)
		} else {
			buckets.set(key, {
				date: new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)),
				items: [item],
			})
		}
	}
	return [...buckets.entries()]
		.sort((a, b) => b[1].date.getTime() - a[1].date.getTime())
		.map(([key, { date, items: monthItems }]) => {
			const sorted = [...monthItems].sort(
				(a, b) => txnSortValue(b.date) - txnSortValue(a.date),
			)
			const total = sorted.reduce((acc, row) => acc + (row.amount || 0), 0)
			return {
				key,
				label: date.toLocaleDateString('en-US', {
					month: 'short',
					year: 'numeric',
					timeZone: 'UTC',
				}),
				depositCount: sorted.length,
				total,
				items: sorted,
			}
		})
}

/** 'Aug 1' — short day label for month-by-month rows. */
export function formatTxnDay(value: string | null | undefined): string {
	if (!value) return '-'
	const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
		? new Date(`${value}T12:00:00Z`)
		: new Date(value)
	if (Number.isNaN(d.getTime())) return value
	return d.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		timeZone: 'America/Toronto',
	})
}

export interface ConnectedFlinksAccount {
	loginId: string
	institution: string | null
	accountHolder: string | null
	maskedAccountNumber: string | null
	accountTitle: string | null
	accountType: string | null
	status: 'pending' | 'success' | 'failed' | 'unknown'
	connectedAt: string | null
	verifiedAt: string | null
	isPrimary: boolean
	accountCount: number | null
}

function normalizeFlinksStatus(raw: unknown): ConnectedFlinksAccount['status'] {
	if (raw === 'pending' || raw === 'success' || raw === 'failed') return raw
	return 'unknown'
}

function asRecord(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return null
	return value as Record<string, unknown>
}

function asString(value: unknown): string | null {
	return typeof value === 'string' && value.trim() ? value.trim() : null
}

/**
 * Read-only connected Flinks logins from verify-income stepData. Prefers
 * `accounts[]`; synthesizes one entry from legacy top-level flinksLoginId.
 * No DB join — uses step `summary` fields written by the Flinks worker.
 */
export function parseConnectedFlinksAccounts(
	verifyIncome: ParsedStepData['verify-income'] | null | undefined,
): ConnectedFlinksAccount[] {
	const step = asRecord(verifyIncome)
	if (!step) return []

	const mapEntry = (
		raw: Record<string, unknown>,
		fallbackLoginId?: string | null,
	): ConnectedFlinksAccount | null => {
		const loginId =
			asString(raw.flinksLoginId) ?? fallbackLoginId ?? asString(raw.loginId)
		if (!loginId) return null
		const summary = asRecord(raw.summary)
		return {
			loginId,
			institution: asString(summary?.institution),
			accountHolder: asString(summary?.holderName),
			maskedAccountNumber: asString(summary?.maskedAccountNumber),
			accountTitle: asString(summary?.accountTitle),
			accountType: asString(summary?.accountType),
			status: normalizeFlinksStatus(raw.status),
			connectedAt: asString(raw.addedAt) ?? asString(raw.verifiedAt),
			verifiedAt: asString(raw.verifiedAt),
			isPrimary: raw.isPrimary === true,
			accountCount:
				typeof summary?.accountCount === 'number' &&
					Number.isFinite(summary.accountCount)
					? summary.accountCount
					: null,
		}
	}

	const accountsRaw = step.accounts
	if (Array.isArray(accountsRaw) && accountsRaw.length > 0) {
		const out: ConnectedFlinksAccount[] = []
		for (const entry of accountsRaw) {
			const rec = asRecord(entry)
			if (!rec) continue
			const mapped = mapEntry(rec)
			if (mapped) out.push(mapped)
		}
		if (out.length > 0) return out
	}

	const legacyLogin = asString(step.flinksLoginId)
	if (legacyLogin) {
		const mapped = mapEntry(step, legacyLogin)
		return mapped ? [mapped] : []
	}
	return []
}

/** Declared reference line for the chart — prefer net monthly, else gross. */
export function declaredReferenceMonthly(
	aiDeclared: AiDeclaredIncome | null,
	employment: ParsedStepData['employment'],
): number | null {
	const current = aiDeclared?.current
	if (current) {
		if (current.netMonthly != null) return current.netMonthly
		if (current.grossMonthly != null) return current.grossMonthly
	}
	return declaredMonthlyIncome(employment)
}

/** Caption under declared tiles, e.g. "$58,000 Salary As Stated On The Application." */
export function declaredSalaryCaption(
	aiDeclared: AiDeclaredIncome | null,
	employment: ParsedStepData['employment'],
): string | null {
	const grossMonthly = aiDeclared?.current?.grossMonthly
	const statedAnnual = declaredAnnualIncome(employment)
	// Some reports put the yearly salary in the Gross box. Multiplying that
	// again shows $955,596 instead of the application figure ($79,633).
	const annual =
		statedAnnual != null &&
			grossMonthly != null &&
			Math.abs(grossMonthly - statedAnnual) < 1
			? statedAnnual
			: grossMonthly != null
				? grossMonthly * 12
				: statedAnnual
	if (annual == null || annual <= 0) return null
	const formatted = Math.round(annual).toLocaleString('en-CA', {
		style: 'currency',
		currency: 'CAD',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	})
	return `${formatted}/Yr Salary As Stated On The Application.`
}

/** Format a connectedAt ISO string for the institutions list
 * ("Connected on Aug 3, 2026, 4:12 PM"). */
export function formatConnectedDate(value: string | null | undefined): string {
	if (!value) return 'Connected date unavailable'
	const d = new Date(value)
	if (Number.isNaN(d.getTime())) return value
	const stamped = d.toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		timeZone: 'America/Toronto',
	})
	return `Connected on ${stamped}`
}

export interface RentPeriod {
	startDate?: string
	endDate?: string | null
	durationLabel?: string
}

/** Extracts and formats the rent period for the current or previous stay. */
export function buildDeclaredRentPeriod(
	rental: ParsedStepData['rental'] | null | undefined,
	periodType: 'current' | 'previous',
): RentPeriod | undefined {
	if (!rental) return undefined

	if (periodType === 'previous') {
		if (!rental.previousMoveInDate && !rental.previousMoveOutDate) return undefined
		return {
			startDate: rental.previousMoveInDate || undefined,
			endDate: rental.previousMoveOutDate || undefined,
		}
	}

	const durationLabel = rental.currentYearsLived
	if (!durationLabel) return undefined

	return {
		startDate: durationToStartDate(durationLabel),
		durationLabel: durationLabel,
	}
}

/** Extracts and formats the employment period for the current or previous job. */
export function buildDeclaredEmploymentPeriod(
	employment: ParsedStepData['employment'] | null | undefined,
	periodType: 'current' | 'previous',
): RentPeriod | undefined {
	if (!employment) return undefined

	if (periodType === 'previous') {
		if (!employment.previousEmploymentStartDate && !employment.previousEmploymentEndDate) return undefined
		return {
			startDate: employment.previousEmploymentStartDate || undefined,
			endDate: employment.previousEmploymentEndDate || undefined,
		}
	}

	const durationLabel = employment.yearsOfEmployment
	if (!durationLabel) return undefined

	return {
		startDate: durationToStartDate(durationLabel),
		durationLabel: durationLabel,
	}
}

export function durationToStartDate(duration: string | null | undefined): string | undefined {
	if (!duration) return undefined
	const trimmed = duration.trim().toLowerCase()
	let y = 0
	let m = 0

	const ymMatch = trimmed.match(/(\d+)\s*years?\s*&?\s*(\d+)\s*months?/)
	if (ymMatch) {
		y = parseInt(ymMatch[1], 10) || 0
		m = parseInt(ymMatch[2], 10) || 0
	} else {
		const yMatch = trimmed.match(/(\d+)\s*years?/)
		if (yMatch) y = parseInt(yMatch[1], 10) || 0

		const mMatch = trimmed.match(/(\d+)\s*months?/)
		if (mMatch) m = parseInt(mMatch[1], 10) || 0
	}

	if (y === 0 && m === 0) return undefined

	const d = new Date()
	d.setFullYear(d.getFullYear() - y)
	d.setMonth(d.getMonth() - m)
	return d.toISOString()
}

export function formatRentPeriod(period?: RentPeriod) {
	if (!period) return null

	if (period.durationLabel && !period.startDate) {
		return { range: '', duration: period.durationLabel }
	}

	if (!period.startDate) return null

	const start = new Date(period.startDate)
	const end = period.endDate ? new Date(period.endDate) : new Date()

	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
		return period.durationLabel ? { range: '', duration: period.durationLabel } : null
	}

	let duration = period.durationLabel
	if (!duration) {
		const months =
			(end.getFullYear() - start.getFullYear()) * 12 +
			(end.getMonth() - start.getMonth()) +
			1

		const y = Math.floor(months / 12)
		const m = months % 12

		duration = ''
		if (y > 0) {
			duration += `${y} ${y === 1 ? 'Year' : 'Years'}`
		}
		if (m > 0) {
			if (duration.length > 0) duration += ' '
			duration += `${m} ${m === 1 ? 'Month' : 'Months'}`
		}
		if (duration === '') {
			duration = '0 Months'
		}
	}

	const formatDate = (date: Date) =>
		date.toLocaleDateString('en-US', {
			month: 'short',
			year: 'numeric',
		})

	const range = `${formatDate(start)} To ${period.endDate ? formatDate(end) : 'Present'
		}`

	return { range, duration }
}

export const RENT_MATCH_PILL_STYLES_V2: Record<
	'strong' | 'possible',
	{ bg: string; text: string; label: string }
> = {
	strong: { bg: '#3FBB781A', text: '#3FBB78', label: 'Strong Match' },
	possible: {
		bg: '#F5A4001A',
		text: '#F5A400',
		label: 'Possible Match',
	},
}